import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEMO_ACCOUNTS, findDemoAccount } from './demoAuth';
import { accessFailure } from './login/accessFailure';
import { loadIdentityProvider, googleLogin, verifyGoogleMfa, readGoogleAccount,
  recoverGooglePassword, signOutIdentity } from './identityClient';

const AuthContext = createContext();
const isLocalHost = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const DEFAULT_API_BASE_URL = isLocalHost
  ? 'http://localhost:3001/api'
  : 'https://web-production-1e53c.up.railway.app/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL;
const DEMO_AUTH_ENABLED = process.env.REACT_APP_ENABLE_DEMO_AUTH === 'true' && isLocalHost;

const readStoredToken = () => {
  const storedToken = localStorage.getItem('token');
  if (!storedToken) return storedToken;
  if (storedToken.startsWith('demo_session_')) {
    if (!DEMO_AUTH_ENABLED) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    }
    return storedToken;
  }

  try {
    const encodedPayload = storedToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = encodedPayload.padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
    const payload = JSON.parse(atob(paddedPayload));
    if (payload.exp && payload.exp * 1000 <= Date.now()) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.setItem('session_expired', 'true');
      return null;
    }
  } catch {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return null;
  }

  return storedToken;
};

const readStoredUser = () => {
  try {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(readStoredToken);
  const [user, setUser] = useState(readStoredUser);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [provider, setProvider] = useState(null);
  const [initializationFailure, setInitializationFailure] = useState(null);
  const demoAuthEnabled = DEMO_AUTH_ENABLED && ready && provider === 'legacy';

  useEffect(() => {
    let live = true;
    loadIdentityProvider().then(async config => {
      if (!live) return;
      setProvider(config.provider);
      if (config.provider === 'google') {
        localStorage.removeItem('token'); localStorage.removeItem('user');
        setToken(null); setUser(null);
        const restored = await readGoogleAccount();
        if (live && restored) { setToken(restored.token); setUser(restored.user); }
      }
    }).catch(failure => {
      if (live) { setToken(null); setUser(null); setError('ACCESS_UNAVAILABLE'); setInitializationFailure(accessFailure(failure, 'initialize')); }
      signOutIdentity().catch(() => {});
    }).finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const handleExpiredSession = () => {
      setToken(null);
      setUser(null);
      signOutIdentity().catch(() => {});
    };
    window.addEventListener('m3s:session-expired', handleExpiredSession);
    return () => window.removeEventListener('m3s:session-expired', handleExpiredSession);
  }, []);

  const login = async (email, password, language = 'fr') => {
    setLoading(true);
    setError('');
    setInitializationFailure(null);

    try {
      const config = await loadIdentityProvider();
      setProvider(config.provider);
      if (config.provider === 'google') {
        const step = await googleLogin(email, password, language);
        if (step.kind === 'mfa') return { success: false, mfaRequired: true };
        const session = await readGoogleAccount();
        if (!session) throw new Error('NO_SESSION');
        setToken(session.token); setUser(session.user);
        return { success: true };
      }
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        const message = 'ACCESS_REJECTED';
        setError(message);
        return { success: false, error: message, failure: accessFailure({ code: response.status === 409 ? 'ACCESS_PROVIDER_CHANGED' :
          response.status === 401 ? 'auth/invalid-credential' : 'ACCESS_UNAVAILABLE' }) };
      }

      setToken(result.token);
      setUser(result.user);
      localStorage.setItem('token', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));

      return { success: true };
    } catch (failure) {
      if (provider === 'google') await signOutIdentity().catch(() => {});
      const message = 'Erreur de connexion';
      setError(message);
      return { success: false, error: message, code: failure.code, failure: accessFailure(failure) };
    } finally {
      setLoading(false);
    }
  };

  const loginDemo = async email => {
    setLoading(true);
    setError('');

    try {
      const config = await loadIdentityProvider();
      const account = demoAuthEnabled && config.provider === 'legacy' ? findDemoAccount(email) : null;
      if (!account) {
        const message = 'Compte de démonstration indisponible';
        setError(message);
        return { success: false, error: message };
      }

      const fakeToken = `demo_session_${Date.now()}_${Math.random()}`;
      const sessionUser = {
        id: account.id,
        tenantId: 'demo-local',
        email,
        name: account.name,
        role: account.role,
        permissions: [...account.permissions]
      };

      setToken(fakeToken);
      setUser(sessionUser);
      localStorage.setItem('token', fakeToken);
      localStorage.setItem('user', JSON.stringify(sessionUser));

      return { success: true };
    } catch {
      const message = 'Erreur de connexion';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    signOutIdentity().catch(() => {});
  };

  const verifyMfa = async code => {
    setLoading(true);
    try {
      await verifyGoogleMfa(code);
      const session = await readGoogleAccount();
      if (!session) throw new Error('NO_SESSION');
      setToken(session.token); setUser(session.user);
      return { success: true };
    } catch (failure) { return { success: false, code: failure.code, failure: accessFailure(failure, 'mfa') }; }
    finally { setLoading(false); }
  };

  const isAuthenticated = Boolean(token && user);

  return (
    <AuthContext.Provider value={{
      token,
      user,
      login,
      loginDemo,
      logout,
      error,
      loading,
      ready,
      provider,
      initializationFailure,
      verifyMfa,
      cancelMfa: () => signOutIdentity(),
      recoverPassword: recoverGooglePassword,
      isAuthenticated,
      demoAuthEnabled,
      demoAccounts: DEMO_ACCOUNTS
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'AuthProvider');
  }
  return context;
};
